import { Moon } from 'lucide-react';
import type { AlertDetailResponse } from '@/types/alerts';
import {
  EvidenceRow,
  HeadlineLine,
  MissingContextLine,
  RendererShell,
  getStr,
} from './_shared';

/**
 * TM-001 — After Hours Fueling.
 *
 * "Transaction occurred outside the configured business window
 * (default 22:00–05:00)." Mostly suppressed for tolls; real signal on
 * fuel because reduced oversight at night is a common fraud vector.
 *
 * Evidence keys (from domain/fraud_rules/time_rules.py AfterHoursRule
 * .create_alert): transaction_time (HH:MM:SS), after_hours_start
 * (HH:MM), after_hours_end (HH:MM).
 */
export default function AfterHoursRenderer(
  evidence: Record<string, unknown> | null | undefined,
  alert: AlertDetailResponse,
): JSX.Element {
  const txnTime = getStr(evidence, 'transaction_time');
  const startTime = getStr(evidence, 'after_hours_start') ?? '22:00';
  const endTime = getStr(evidence, 'after_hours_end') ?? '05:00';
  const vehicleReg = alert.asset_token;

  // Trim seconds for the headline if the format is HH:MM:SS
  const txnDisplay = txnTime?.length === 8 ? txnTime.slice(0, 5) : txnTime;

  return (
    <RendererShell>
      <div className="flex items-start gap-2">
        <Moon className="mt-0.5 h-4 w-4 flex-shrink-0 text-indigo-600" />
        <HeadlineLine>
          Transaction occurred at{' '}
          <span className="text-red-700">{txnDisplay ?? 'an unknown time'}</span>
          {' '}— outside the {startTime}–{endTime} business window.
        </HeadlineLine>
      </div>

      <div className="rounded-md border bg-muted/20 px-3 py-2">
        <EvidenceRow label="Transaction time">
          <span className="font-mono">{txnTime ?? '—'}</span>
        </EvidenceRow>
        <EvidenceRow label="After-hours window">
          {startTime} – {endTime}
        </EvidenceRow>
        <EvidenceRow label="Vehicle">
          <span className="font-mono text-xs">{vehicleReg ?? '—'}</span>
        </EvidenceRow>
        <EvidenceRow label="Transaction kind">
          {alert.transaction_kind ?? '—'}
        </EvidenceRow>
      </div>

      {!txnTime && (
        <MissingContextLine message="Transaction time is missing — see raw evidence below." />
      )}
    </RendererShell>
  );
}
